import moment from 'moment';
import { Reducer } from 'redux';
import { IInvoiceBilletResponse } from '../invoices/types';
import {
  ELongSteelOrdersStatus,
  IBilledOrderData,
  IDownloadNFEResponse,
  IQCFileResponse
} from '../long-steel-orders/types';
import {
  ICustomerBilletItem,
  ICustomerNFItem,
  ICustomerQualityCertificateItem,
  IDownloadAreaState,
  Types
} from './types';

const startDate = moment().subtract(90, 'days').format('YYYY-MM-DD');
const endDate = moment().format('YYYY-MM-DD');

export const INITIAL_STATE: IDownloadAreaState = {
  customerId: '',
  startDate,
  endDate,
  nfsState: {
    loading: false,
    error: false,
    items: []
  },
  billetsState: {
    loading: false,
    error: false,
    items: []
  },
  qualityCertificatesState: {
    loading: false,
    error: false,
    items: []
  }
};

export type CustomActionPayload = {
  customerId: string;
  startDate: string;
  endDate: string;
  billedOrders: IBilledOrderData[];
  billets: ICustomerBilletItem[];
  billetItem: ICustomerBilletItem;
  invoiceBilletResponse: IInvoiceBilletResponse;
  qualityCertificateItem: ICustomerQualityCertificateItem;
  qcFileResponse: IQCFileResponse;
  nfItem: ICustomerNFItem;
  downloadNFEResponse: IDownloadNFEResponse;
  nfXmlItem: ICustomerNFItem;
};

type CustomAction = {
  type: Types;
  payload: CustomActionPayload;
};

const reducer: Reducer<IDownloadAreaState, CustomAction> = (
  state = INITIAL_STATE,
  action
) => {
  switch (action.type) {
    case Types.LOAD_CUSTOMER_NFS_AND_QUALITY_CERTIFICATES:
      return {
        ...state,
        customerId: action.payload.customerId,
        endDate: action.payload.endDate,
        startDate: action.payload.startDate,
        nfsState: {
          items: [],
          error: false,
          loading: true
        },
        qualityCertificatesState: {
          items: [],
          error: false,
          loading: true
        }
      };
    case Types.LOAD_CUSTOMER_NFS_AND_QUALITY_CERTIFICATES_SUCCESS:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          loading: false,
          items: getCustomerNFsFromBilledOrders(action.payload.billedOrders)
        },
        qualityCertificatesState: {
          ...state.qualityCertificatesState,
          loading: false,
          items: getCustomerQualityCertificatesFromBilledOrders(
            action.payload.billedOrders
          )
        }
      };
    case Types.LOAD_CUSTOMER_NFS_AND_QUALITY_CERTIFICATES_FAILURE:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          loading: false,
          error: true
        },
        qualityCertificatesState: {
          ...state.qualityCertificatesState,
          loading: false,
          error: true
        }
      };
    case Types.LOAD_CUSTOMER_BILLETS:
      return {
        ...state,
        customerId: action.payload.customerId,
        endDate: action.payload.endDate,
        startDate: action.payload.startDate,
        billetsState: {
          items: [],
          error: false,
          loading: true
        }
      };
    case Types.LOAD_CUSTOMER_BILLETS_SUCCESS:
      return {
        ...state,
        billetsState: {
          ...state.billetsState,
          loading: false,
          items: action.payload.billets
        }
      };
    case Types.LOAD_CUSTOMER_BILLETS_FAILURE:
      return {
        ...state,
        billetsState: {
          ...state.billetsState,
          loading: false,
          error: true
        }
      };

    case Types.LOAD_BILLET_FILE:
      return {
        ...state,
        billetsState: {
          ...state.billetsState,
          items: setBilletStateInBilletItem(
            state.billetsState.items,
            action.payload.billetItem,
            true,
            false
          )
        }
      };
    case Types.LOAD_BILLET_FILE_SUCCESS:
      return {
        ...state,
        billetsState: {
          ...state.billetsState,
          items: setBilletStateInBilletItem(
            state.billetsState.items,
            action.payload.billetItem,
            false,
            false,
            action.payload.invoiceBilletResponse
          )
        }
      };
    case Types.LOAD_BILLET_FILE_FAILURE:
      return {
        ...state,
        billetsState: {
          ...state.billetsState,
          items: setBilletStateInBilletItem(
            state.billetsState.items,
            action.payload.billetItem,
            false,
            true
          )
        }
      };

    case Types.DOWNLOAD_BILLET_FILE:
      return {
        ...state,
        billetsState: {
          ...state.billetsState,
          items: setBilletStateInBilletItem(
            state.billetsState.items,
            action.payload.billetItem,
            false,
            true,
            action.payload.billetItem.billetState?.invoiceBilletResponse,
            true
          )
        }
      };

    case Types.LOAD_NF_FILE:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFStateInItem(
            state.nfsState.items,
            action.payload.nfItem,
            true,
            false
          )
        }
      };

    case Types.LOAD_NF_FILE_SUCCESS:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFStateInItem(
            state.nfsState.items,
            action.payload.nfItem,
            false,
            false,
            action.payload.downloadNFEResponse
          )
        }
      };

    case Types.LOAD_NF_FILE_FAILURE:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFStateInItem(
            state.nfsState.items,
            action.payload.nfItem,
            false,
            true
          )
        }
      };

    case Types.DOWNLOAD_NF_FILE:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFStateInItem(
            state.nfsState.items,
            action.payload.nfItem,
            false,
            false,
            action.payload.nfItem.nfState?.downloadNFEResponse,
            true
          )
        }
      };

    case Types.CLEAN_DOWNLOAD_AREA_STATE:
      return INITIAL_STATE;
    case Types.LOAD_QC_FILE: {
      return {
        ...state,
        qualityCertificatesState: {
          ...state.qualityCertificatesState,
          items: setQualityCertificatesStateInQualityCertificateItem(
            state.qualityCertificatesState.items,
            action.payload.qualityCertificateItem,
            true,
            false
          )
        }
      };
    }
    case Types.LOAD_QC_FILE_SUCCESS: {
      return {
        ...state,
        qualityCertificatesState: {
          ...state.qualityCertificatesState,
          items: setQualityCertificatesStateInQualityCertificateItem(
            state.qualityCertificatesState.items,
            action.payload.qualityCertificateItem,
            false,
            false,
            action.payload.qcFileResponse
          )
        }
      };
    }
    case Types.LOAD_QC_FILE_FAILURE: {
      return {
        ...state,
        qualityCertificatesState: {
          ...state.qualityCertificatesState,
          items: setQualityCertificatesStateInQualityCertificateItem(
            state.qualityCertificatesState.items,
            action.payload.qualityCertificateItem,
            false,
            true
          )
        }
      };
    }
    case Types.DOWNLOAD_QC_FILE: {
      return {
        ...state,
        qualityCertificatesState: {
          ...state.qualityCertificatesState,
          items: setQualityCertificatesStateInQualityCertificateItem(
            state.qualityCertificatesState.items,
            action.payload.qualityCertificateItem,
            false,
            false,
            action.payload.qualityCertificateItem.qcState
              ?.qcFileResponse as IQCFileResponse,
            true
          )
        }
      };
    }
    case Types.LOAD_NF_XML_FILE:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFXMLStateInItem(
            state.nfsState.items,
            action.payload.nfXmlItem,
            true,
            false
          )
        }
      };

    case Types.LOAD_NF_XML_FILE_SUCCESS:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFXMLStateInItem(
            state.nfsState.items,
            action.payload.nfXmlItem,
            false,
            false,
            action.payload.downloadNFEResponse
          )
        }
      };

    case Types.LOAD_NF_XML_FILE_FAILURE:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFXMLStateInItem(
            state.nfsState.items,
            action.payload.nfXmlItem,
            false,
            true
          )
        }
      };

    case Types.DOWNLOAD_NF_XML_FILE:
      return {
        ...state,
        nfsState: {
          ...state.nfsState,
          items: setNFXMLStateInItem(
            state.nfsState.items,
            action.payload.nfXmlItem,
            false,
            false,
            action.payload.nfXmlItem.nfXmlState?.downloadNFEResponse,
            true
          )
        }
      };
    default:
      return state;
  }
};

const getCustomerNFsFromBilledOrders = (
  billedOrders: IBilledOrderData[]
): ICustomerNFItem[] => {
  return billedOrders.map((billedOrder, idx) => {
    return {
      billingNumber: billedOrder.billingNumber,
      key: idx,
      danfeNumber: billedOrder.danfeNumber,
      invoiceNumber: billedOrder.invoiceNumber,
      itemBillingDate: billedOrder.itemBillingDate,
      totalValue: billedOrder.conditions.invoiceItemValueWithTaxes,
      orderNumber: billedOrder.billingHistory.salesOrder.toString(),
      status: ELongSteelOrdersStatus.BILLED
    };
  });
};

const getCustomerQualityCertificatesFromBilledOrders = (
  billedOrders: IBilledOrderData[]
): ICustomerQualityCertificateItem[] => {
  return billedOrders.map((billedOrder, idx) => {
    const plantsInitialValueNotAllowed = ['24'];
    const divisionNotAllowed = [
      '60',
      '62',
      '63',
      '64',
      '69',
      '70',
      '71',
      '73',
      '74',
      '75'
    ];
    let blockedDivision = false;
    if ('BRCG' == billedOrder.billingHistory.salesOrganization.toUpperCase()) {
      for (const na of divisionNotAllowed) {
        if (billedOrder.billingHistory.division == na) {
          blockedDivision = true;
          break;
        }
      }
    }

    if (
      plantsInitialValueNotAllowed.includes(
        billedOrder.billingHistory.plant.slice(0, 2)
      )
    ) {
      blockedDivision = true;
    }
    return {
      orderNumber: billedOrder.billingHistory.salesOrder.toString(),
      invoiceNumber: billedOrder.invoiceNumber,
      sku: billedOrder.billingHistory.sku.toString(),
      customerSKU: billedOrder.billingHistory.customerSKU,
      itemBillingDate: billedOrder.itemBillingDate,
      materialDescription: billedOrder.billingHistory.materialDescription,
      canDownloadQC: !blockedDivision
        ? billedOrder.qcState.canDownloadQC
        : false,
      key: idx,
      cqNumber: billedOrder.cqNumber,
      deliveryCode: billedOrder.deliveryCode,
      deliveryItemCode: billedOrder.deliveryItemCode
    };
  });
};

const setBilletStateInBilletItem = (
  items: ICustomerBilletItem[],
  selectedItem: ICustomerBilletItem,
  loading: boolean,
  error: boolean,
  invoiceBilletResponse?: IInvoiceBilletResponse,
  alreadyDownloaded?: boolean
): ICustomerBilletItem[] => {
  const selectedItemIdx = items.findIndex(
    (item) => item.key === selectedItem.key
  );

  if (selectedItemIdx !== -1) {
    items[selectedItemIdx].billetState = {
      loading: loading,
      error: error,
      invoiceBilletResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setQualityCertificatesStateInQualityCertificateItem = (
  items: ICustomerQualityCertificateItem[],
  selectedItem: ICustomerQualityCertificateItem,
  loading: boolean,
  error: boolean,
  qcFileResponse?: IQCFileResponse,
  alreadyDownloaded?: boolean
): ICustomerQualityCertificateItem[] => {
  const selectedItemIdx = items.findIndex(
    (item) => item.key === selectedItem.key
  );

  if (selectedItemIdx !== -1) {
    items[selectedItemIdx].qcState = {
      ...items[selectedItemIdx].qcState,
      loading,
      error,
      qcFileResponse,
      alreadyDownloaded
    };
  }
  return items;
};

const setNFStateInItem = (
  items: ICustomerNFItem[],
  selectedItem: ICustomerNFItem,
  loading: boolean,
  error: boolean,
  downloadNFEResponse?: IDownloadNFEResponse,
  alreadyDownloaded?: boolean
): ICustomerNFItem[] => {
  const selectedItemIdx = items.findIndex(
    (item) => item.key === selectedItem.key
  );
  if (selectedItemIdx !== -1) {
    items[selectedItemIdx].nfState = {
      loading,
      error,
      downloadNFEResponse,
      alreadyDownloaded,
      canDownloadNF: true
    };
  }
  return items;
};

const setNFXMLStateInItem = (
  items: ICustomerNFItem[],
  selectedItem: ICustomerNFItem,
  loading: boolean,
  error: boolean,
  downloadNFEResponse?: IDownloadNFEResponse,
  alreadyDownloaded?: boolean
): ICustomerNFItem[] => {
  const selectedItemIdx = items.findIndex(
    (item) => item.key === selectedItem.key
  );
  if (selectedItemIdx !== -1) {
    items[selectedItemIdx].nfXmlState = {
      loading,
      error,
      downloadNFEResponse,
      alreadyDownloaded,
      canDownloadNF: true
    };
  }
  return items;
};

export default reducer;
